import React, { useState, ChangeEvent } from 'react';
import {
  SimpleShowLayout,
  Record,
  useTranslate,
  useNotify,
  useRedirect,
  useMutation,
} from 'react-admin';
import { Button, CircularProgress, TextField } from '@material-ui/core';
import SectionTitle from '../SectionTitle';
import CustomSelect from '../CustomSelect';
// import CustomCheckbox from '../CustomCheckbox';
import messages from '../DoctorReportForm/messages';
import ConfirmationModal from '../DoctorReportForm/ConfirmationModal';
import { PartnerCodes } from '../../constants/partner/partnerCodes';

import './styles.scss';

interface WeightLossTreatmentCheckConfiguratorProps {
  record: Record | undefined;
  readOnly?: boolean;
  bmi: string;
}

const getDoctorReport = (record: Record | undefined) => {
  if (!record) {
    return null;
  }

  const { services } = record.results;

  const doctorReport = services.find(
    (service: any) => service.service === 'doctorsReport',
  );

  return doctorReport;
};

const getDoctorsName = (record: any) => {
  const { doctorReport, partnerCode } = record || {};
  if (doctorReport && doctorReport.doctorsName) {
    return doctorReport.doctorsName;
  }

  if (partnerCode && partnerCode === PartnerCodes.STEFAN_ZECHMANN) {
    return 'Dr. Stefan Zechmann';
  }

  return 'Dr. Robert Döring';
};

const checkRecommendedAction = (recommendedActions: any, key: string) => {
  if (!recommendedActions) {
    return '';
  }

  const actionData = recommendedActions.find(
    (action: any) => action.key === key,
  );
  if (actionData) {
    return 'true';
  }

  return 'false';
};

const WeightLossTreatmentCheckConfigurator: React.FC<WeightLossTreatmentCheckConfiguratorProps> =
  ({ record, readOnly = false, bmi }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [openModal, setOpenModal] = useState<boolean>(false);
    const recommendedActions = record?.recommendedActions;
    // The habit change program is delivered by Dr. Stefan Zechmann. For every
    // other partner we sell nutrition advice instead, so the treatment option
    // and its recommended action are swapped throughout the form.
    const isZechmannPartner =
      record?.partnerCode === PartnerCodes.STEFAN_ZECHMANN;

    const checkIfPersonIsOverweight = () => {
      if (record?.doctorDiagnosis?.isPersonOverweight) {
        return record?.doctorDiagnosis?.isPersonOverweight;
      }

      if (!bmi) {
        return '';
      }

      if (bmi && parseFloat(bmi) >= 25) {
        return 'true';
      }

      return 'false';
    };

    const doctorReport = getDoctorReport(record);
    // Configurations
    const [isPersonOverweight, setIsPersonOverweight] = useState<string>(
      checkIfPersonIsOverweight(),
    );
    // Cause of being overweight
    const [hypothyroidism, setHypothyroidism] = useState<string>(
      record?.doctorDiagnosis?.hypothyroidism || '',
    );
    // Indentified health impact of being overweight
    const [elevatedBloodLipids, setElevatedBloodLipids] = useState<string>(
      record?.doctorDiagnosis?.elevatedBloodLipids || '',
    );
    const [elevatedLipoprotein, setElevatedLipoprotein] = useState<string>(
      record?.doctorDiagnosis?.elevatedLipoprotein || '',
    );
    const [elevatedBloodSugar, setElevatedBloodSugar] = useState<string>(
      record?.doctorDiagnosis?.elevatedBloodSugar || '',
    );
    const [indicationForFattyLiver, setIndicationForFattyLiver] =
      useState<string>(record?.doctorDiagnosis?.indicationForFattyLiver || '');
    // Treatment options
    const [habitChangeProgram, setHabitChangeProgram] = useState<string>(
      record?.doctorDiagnosis?.habitChangeProgram || '',
    );
    const [
      reasonsForNotEligibleForHabitChangeProgram,
      setReasonsForNotEligibleForHabitChangeProgram,
    ] = useState<string>(
      record?.doctorDiagnosis?.reasonsForNotEligibleForHabitChangeProgram || '',
    );

    const [
      customReasonForNotEligibleForHabitChangeProgram,
      setCustomReasonForNotEligibleForHabitChangeProgram,
    ] = useState<string>(
      record?.doctorDiagnosis
        ?.customReasonForNotEligibleForHabitChangeProgram || '',
    );

    const [nutritionAdviceProgram, setNutritionAdviceProgram] =
      useState<string>(record?.doctorDiagnosis?.nutritionAdviceProgram || '');

    const [
      reasonsForNotEligibleForNutritionAdviceProgram,
      setReasonsForNotEligibleForNutritionAdviceProgram,
    ] = useState<string>(
      record?.doctorDiagnosis?.reasonsForNotEligibleForNutritionAdviceProgram ||
        '',
    );

    const [
      customReasonForNotEligibleForNutritionAdviceProgram,
      setCustomReasonForNotEligibleForNutritionAdviceProgram,
    ] = useState<string>(
      record?.doctorDiagnosis
        ?.customReasonForNotEligibleForNutritionAdviceProgram || '',
    );

    const [weightLossInjections, setWeightLossInjections] = useState<string>(
      record?.doctorDiagnosis?.weightLossInjections || '',
    );
    const [
      reasonsForNotEligibleForWeightLossInjections,
      setReasonsForNotEligibleForWeightLossInjections,
    ] = useState<string>(
      record?.doctorDiagnosis?.reasonsForNotEligibleForWeightLossInjections ||
        '',
    );

    const [
      customReasonForNotEligibleForWeightLossInjections,
      setCustomReasonForNotEligibleForWeightLossInjections,
    ] = useState<string>(
      record?.doctorDiagnosis
        ?.customReasonForNotEligibleForWeightLossInjections || '',
    );

    // Reimbursement
    const [hasReimbursement, setHasReimbursement] = useState<string>(
      record?.doctorDiagnosis?.hasReimbursement || '',
    );

    // Recommended actions
    const [consultDoctor, setConsultDoctor] = useState<string>(
      checkRecommendedAction(recommendedActions, 'consultDoctor'),
    );
    const [
      bookConsultationForWeightLossMedication,
      setBookConsultationForWeightLossMedication,
    ] = useState<string>(
      checkRecommendedAction(
        recommendedActions,
        'bookConsultationForWeightLossMedication',
      ),
    );

    const [
      bookConsultationForWeightLossHabitChangeProgram,
      setBookConsultationForWeightLossHabitChangeProgram,
    ] = useState<string>(
      checkRecommendedAction(
        recommendedActions,
        'bookConsultationForWeightLossHabitChangeProgram',
      ),
    );

    const [
      bookConsultationForNutritionAdvice,
      setBookConsultationForNutritionAdvice,
    ] = useState<string>(
      checkRecommendedAction(
        recommendedActions,
        'bookConsultationForNutritionAdvice',
      ),
    );

    // const [consultationCompleted, setConsultationCompleted] =
    //   useState<boolean>();

    // Report form states
    const [doctorName, setDoctorName] = useState<string>(
      getDoctorsName(record),
    );

    const [report, setReport] = useState<string>(
      doctorReport ? doctorReport.doctorsReport : '',
    );

    // Add error states
    const [errors, setErrors] = useState({
      doctorName: false,
      report: false,
      consultDoctor: false,
      bookConsultationForWeightLossMedication: false,
      bookConsultationForWeightLossHabitChangeProgram: false,
      bookConsultationForNutritionAdvice: false,
      // consultationCompleted: false,
      isPersonOverweight: false,
      hypothyroidism: false,
      elevatedBloodLipids: false,
      elevatedLipoprotein: false,
      elevatedBloodSugar: false,
      indicationForFattyLiver: false,
      habitChangeProgram: false,
      nutritionAdviceProgram: false,
      weightLossInjections: false,
      reasonsForNotEligibleForHabitChangeProgram: false,
      reasonsForNotEligibleForNutritionAdviceProgram: false,
      reasonsForNotEligibleForWeightLossInjections: false,
      hasReimbursement: false,
      customReasonForNotEligibleForHabitChangeProgram: false,
      customReasonForNotEligibleForNutritionAdviceProgram: false,
      customReasonForNotEligibleForWeightLossInjections: false,
    });

    const createRecommendedActionsArray = () => {
      const actions = [];

      if (consultDoctor === 'true') {
        actions.push({
          done: false,
          key: 'consultDoctor',
          timingKey: 'now',
        });
      }

      if (bookConsultationForWeightLossMedication === 'true') {
        actions.push({
          done: false,
          key: 'bookConsultationForWeightLossMedication',
          timingKey: 'now',
        });
      }

      if (
        isZechmannPartner &&
        bookConsultationForWeightLossHabitChangeProgram === 'true'
      ) {
        actions.push({
          done: false,
          key: 'bookConsultationForWeightLossHabitChangeProgram',
          timingKey: 'now',
        });
      }

      if (!isZechmannPartner && bookConsultationForNutritionAdvice === 'true') {
        actions.push({
          done: false,
          key: 'bookConsultationForNutritionAdvice',
          timingKey: 'now',
        });
      }

      return actions;
    };

    // Only send the program the doctor was actually asked about — the backend
    // persists a program field only when it is present in the payload.
    const getDoctorDiagnosis = () => ({
      isPersonOverweight,
      hypothyroidism,
      elevatedBloodLipids,
      elevatedLipoprotein,
      elevatedBloodSugar,
      indicationForFattyLiver,
      weightLossInjections,
      reasonsForNotEligibleForWeightLossInjections,
      hasReimbursement,
      customReasonForNotEligibleForWeightLossInjections,
      ...(isZechmannPartner
        ? {
            habitChangeProgram,
            reasonsForNotEligibleForHabitChangeProgram,
            customReasonForNotEligibleForHabitChangeProgram,
          }
        : {
            nutritionAdviceProgram,
            reasonsForNotEligibleForNutritionAdviceProgram,
            customReasonForNotEligibleForNutritionAdviceProgram,
          }),
    });

    const [addReport, { loading }] = useMutation(
      {
        type: 'update',
        resource: 'tests',
        payload: {
          id: record?.id,
          data: {
            report,
            doctorName,
            isPharmacyReport: false,
            pharmacyName: null,
            recommendedActions: createRecommendedActionsArray(),
            doctorDiagnosis: getDoctorDiagnosis(),
          },
        },
      },
      {
        onSuccess: () => {
          redirect('/tests/weight-loss-treatment-check');
          notify(messages.addReportSuccessMessage, { type: 'success' });
        },
        onFailure: () =>
          notify(messages.addReportErrorMessage, { type: 'warning' }),
      },
    );

    const validateFields = () => {
      const newErrors = {
        doctorName: !doctorName || doctorName.trim() === '',
        report:
          isPersonOverweight === 'false' && (!report || report.trim() === ''),
        consultDoctor: !consultDoctor,
        bookConsultationForWeightLossMedication:
          isPersonOverweight === 'true' &&
          !bookConsultationForWeightLossMedication,
        bookConsultationForWeightLossHabitChangeProgram:
          isZechmannPartner &&
          isPersonOverweight === 'true' &&
          !bookConsultationForWeightLossHabitChangeProgram,
        bookConsultationForNutritionAdvice:
          !isZechmannPartner &&
          isPersonOverweight === 'true' &&
          !bookConsultationForNutritionAdvice,
        // consultationCompleted: !consultationCompleted,
        isPersonOverweight: !isPersonOverweight,
        hypothyroidism: isPersonOverweight === 'true' && !hypothyroidism,
        elevatedBloodLipids:
          isPersonOverweight === 'true' && !elevatedBloodLipids,
        elevatedLipoprotein:
          isPersonOverweight === 'true' && !elevatedLipoprotein,
        elevatedBloodSugar:
          isPersonOverweight === 'true' && !elevatedBloodSugar,
        indicationForFattyLiver:
          isPersonOverweight === 'true' && !indicationForFattyLiver,
        habitChangeProgram:
          isZechmannPartner &&
          isPersonOverweight === 'true' &&
          !habitChangeProgram,
        nutritionAdviceProgram:
          !isZechmannPartner &&
          isPersonOverweight === 'true' &&
          !nutritionAdviceProgram,
        weightLossInjections:
          isPersonOverweight === 'true' && !weightLossInjections,
        reasonsForNotEligibleForHabitChangeProgram:
          isZechmannPartner &&
          habitChangeProgram === 'false' &&
          !reasonsForNotEligibleForHabitChangeProgram,
        reasonsForNotEligibleForNutritionAdviceProgram:
          !isZechmannPartner &&
          nutritionAdviceProgram === 'false' &&
          !reasonsForNotEligibleForNutritionAdviceProgram,
        reasonsForNotEligibleForWeightLossInjections:
          weightLossInjections === 'false' &&
          !reasonsForNotEligibleForWeightLossInjections,
        hasReimbursement: isPersonOverweight === 'true' && !hasReimbursement,
        customReasonForNotEligibleForHabitChangeProgram:
          isZechmannPartner &&
          habitChangeProgram === 'false' &&
          reasonsForNotEligibleForHabitChangeProgram === 'other' &&
          !customReasonForNotEligibleForHabitChangeProgram,
        customReasonForNotEligibleForNutritionAdviceProgram:
          !isZechmannPartner &&
          nutritionAdviceProgram === 'false' &&
          reasonsForNotEligibleForNutritionAdviceProgram === 'other' &&
          !customReasonForNotEligibleForNutritionAdviceProgram,
        customReasonForNotEligibleForWeightLossInjections:
          weightLossInjections === 'false' &&
          reasonsForNotEligibleForWeightLossInjections === 'other' &&
          !customReasonForNotEligibleForWeightLossInjections,
      };

      setErrors(newErrors);
      return !Object.values(newErrors).some((error) => error);
    };

    const handleFormSubmit = () => {
      const isValid = validateFields();

      if (!isValid) {
        setOpenModal(false);
        return;
      }

      if (isValid) {
        addReport();
      }
    };

    // Select handlers
    const handleConsultDoctorChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setConsultDoctor(event.target.value);
      setErrors((prev) => ({ ...prev, consultDoctor: false }));
    };

    const handleBookConsultationForWeightLossHabitChangeProgramChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setBookConsultationForWeightLossHabitChangeProgram(event.target.value);
      setErrors((prev) => ({
        ...prev,
        bookConsultationForWeightLossHabitChangeProgram: false,
      }));
    };

    const handleBookConsultationForNutritionAdviceChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setBookConsultationForNutritionAdvice(event.target.value);
      setErrors((prev) => ({
        ...prev,
        bookConsultationForNutritionAdvice: false,
      }));
    };

    const handleBookConsultationForWeightLossMedicationChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setBookConsultationForWeightLossMedication(event.target.value);
      setErrors((prev) => ({
        ...prev,
        bookConsultationForWeightLossMedication: false,
      }));
    };

    // Report form handlers
    const handleDoctorNameInputChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      setDoctorName(event.target.value);
      setErrors((prev) => ({ ...prev, doctorName: false }));
    };

    const handleReportTextareaChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      setReport(event.target.value);
      setErrors((prev) => ({ ...prev, report: false }));
    };

    // const handleConsultationCompletedChange = (checked: boolean) => {
    //   setConsultationCompleted(checked);
    //   setErrors((prev) => ({ ...prev, consultationCompleted: false }));
    // };

    const handleIsPersonOverweightChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setIsPersonOverweight(event.target.value);
      setErrors((prev) => ({ ...prev, isPersonOverweight: false }));
    };

    const handleHypothyroidismChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setHypothyroidism(event.target.value);
      setErrors((prev) => ({ ...prev, hypothyroidism: false }));
    };

    const handleElevatedBloodLipidsChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setElevatedBloodLipids(event.target.value);
      setErrors((prev) => ({ ...prev, elevatedBloodLipids: false }));
    };

    const handleElevatedLipoproteinChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setElevatedLipoprotein(event.target.value);
      setErrors((prev) => ({ ...prev, elevatedLipoprotein: false }));
    };

    const handleElevatedBloodSugarChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setElevatedBloodSugar(event.target.value);
      setErrors((prev) => ({ ...prev, elevatedBloodSugar: false }));
    };

    const handleIndicationForFattyLiverChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setIndicationForFattyLiver(event.target.value);
      setErrors((prev) => ({ ...prev, indicationForFattyLiver: false }));
    };

    const handleHabitChangeProgramChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setHabitChangeProgram(event.target.value);
      setErrors((prev) => ({ ...prev, habitChangeProgram: false }));
    };

    const handleNutritionAdviceProgramChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setNutritionAdviceProgram(event.target.value);
      setErrors((prev) => ({ ...prev, nutritionAdviceProgram: false }));
    };

    const handleReasonsForNotEligibleForNutritionAdviceProgramChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setReasonsForNotEligibleForNutritionAdviceProgram(event.target.value);
      setErrors((prev) => ({
        ...prev,
        reasonsForNotEligibleForNutritionAdviceProgram: false,
      }));
    };

    const handleCustomReasonForNotEligibleForNutritionAdviceProgramChange = (
      event: ChangeEvent<HTMLInputElement>,
    ) => {
      setCustomReasonForNotEligibleForNutritionAdviceProgram(
        event.target.value,
      );
      setErrors((prev) => ({
        ...prev,
        customReasonForNotEligibleForNutritionAdviceProgram: false,
      }));
    };

    const handleWeightLossInjectionsChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setWeightLossInjections(event.target.value);
      setErrors((prev) => ({ ...prev, weightLossInjections: false }));
    };

    const handleReasonsForNotEligibleForHabitChangeProgramChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setReasonsForNotEligibleForHabitChangeProgram(event.target.value);
      setErrors((prev) => ({
        ...prev,
        reasonsForNotEligibleForHabitChangeProgram: false,
      }));
    };

    const handleCustomReasonForNotEligibleForHabitChangeProgramChange = (
      event: ChangeEvent<HTMLInputElement>,
    ) => {
      setCustomReasonForNotEligibleForHabitChangeProgram(event.target.value);
      setErrors((prev) => ({
        ...prev,
        customReasonForNotEligibleForHabitChangeProgram: false,
      }));
    };

    const handleReasonsForNotEligibleForWeightLossInjectionsChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setReasonsForNotEligibleForWeightLossInjections(event.target.value);
      setErrors((prev) => ({
        ...prev,
        reasonsForNotEligibleForWeightLossInjections: false,
      }));
    };

    const handleCustomReasonForNotEligibleForWeightLossInjectionsChange = (
      event: ChangeEvent<HTMLInputElement>,
    ) => {
      setCustomReasonForNotEligibleForWeightLossInjections(event.target.value);
      setErrors((prev) => ({
        ...prev,
        customReasonForNotEligibleForWeightLossInjections: false,
      }));
    };

    const handleHasReimbursementChange = (
      event: ChangeEvent<HTMLSelectElement>,
    ) => {
      setHasReimbursement(event.target.value);
      setErrors((prev) => ({ ...prev, hasReimbursement: false }));
    };

    const handleCloseModal = () => {
      setOpenModal(false);
    };

    const handleOpenModal = (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setOpenModal(true);
    };

    return (
      <SimpleShowLayout className="weightLossTreatmentCheckConfigurator">
        <>
          <ConfirmationModal
            handleClose={handleCloseModal}
            open={openModal}
            confirmationFunction={handleFormSubmit}
            loading={loading || false}
            isFertilityCheck
          />
          <form onSubmit={handleOpenModal}>
            <div className="weightLossTreatmentCheckConfigurator__section">
              <SectionTitle
                className="weightLossTreatmentCheckConfigurator__title"
                title={translate('testResults.diagnosis')}
              />
              <div className="weightLossTreatmentCheckConfigurator__item">
                <div>
                  {translate(
                    'weightLossTreatmentCheck.configurator.isPersonOverweight',
                  )}
                  <span className="weightLossTreatmentCheckConfigurator__item--explanation">
                    {translate(
                      'weightLossTreatmentCheck.configurator.overweightExplanation',
                    )}
                  </span>
                </div>
                <CustomSelect
                  emptyLabel={translate(
                    'weightLossTreatmentCheck.configurator.yesOrNo',
                  )}
                  options={[
                    {
                      value: 'true',
                      label: translate(
                        'weightLossTreatmentCheck.configurator.yes',
                      ),
                    },
                    {
                      value: 'false',
                      label: translate(
                        'weightLossTreatmentCheck.configurator.no',
                      ),
                    },
                  ]}
                  value={isPersonOverweight}
                  onChange={handleIsPersonOverweightChange}
                  error={errors.isPersonOverweight}
                  disabled={readOnly}
                />
              </div>
              {/* We are showing this if the person is overweight */}
              {isPersonOverweight === 'true' && (
                <>
                  <div className="weightLossTreatmentCheckConfigurator__section">
                    <div className="weightLossTreatmentCheckConfigurator__item--title">
                      {translate(
                        'weightLossTreatmentCheck.configurator.causeOfBeingOverweight',
                      )}
                    </div>
                    <div className="weightLossTreatmentCheckConfigurator__item">
                      <div className="weightLossTreatmentCheckConfigurator__item--title-wrapper">
                        {translate(
                          'weightLossTreatmentCheck.configurator.hypothyroidism',
                        )}
                        <span className="weightLossTreatmentCheckConfigurator__item--explanation">
                          {translate(
                            'weightLossTreatmentCheck.configurator.hypothyroidismExplanation',
                          )}
                        </span>
                      </div>
                      <CustomSelect
                        emptyLabel={translate(
                          'weightLossTreatmentCheck.configurator.nothingSelected',
                        )}
                        options={[
                          {
                            value: 'true',
                            label: translate(
                              'weightLossTreatmentCheck.configurator.detected',
                            ),
                          },
                          {
                            value: 'false',
                            label: translate(
                              'weightLossTreatmentCheck.configurator.notDetected',
                            ),
                          },
                        ]}
                        value={hypothyroidism}
                        onChange={handleHypothyroidismChange}
                        error={errors.hypothyroidism}
                        disabled={readOnly}
                      />
                    </div>
                  </div>
                  <div className="weightLossTreatmentCheckConfigurator__section">
                    <div className="weightLossTreatmentCheckConfigurator__item--title">
                      {translate(
                        'weightLossTreatmentCheck.configurator.identifiedHealthImpactOfBeingOverweight',
                      )}
                    </div>
                    <div className="weightLossTreatmentCheckConfigurator__item">
                      <div>
                        {translate(
                          'weightLossTreatmentCheck.configurator.elevatedBloodLipids',
                        )}
                      </div>
                      <CustomSelect
                        emptyLabel={translate(
                          'weightLossTreatmentCheck.configurator.nothingSelected',
                        )}
                        options={[
                          {
                            value: 'true',
                            label: translate(
                              'weightLossTreatmentCheck.configurator.detected',
                            ),
                          },
                          {
                            value: 'false',
                            label: translate(
                              'weightLossTreatmentCheck.configurator.notDetected',
                            ),
                          },
                        ]}
                        value={elevatedBloodLipids}
                        onChange={handleElevatedBloodLipidsChange}
                        error={errors.elevatedBloodLipids}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="weightLossTreatmentCheckConfigurator__item">
                      <div className="weightLossTreatmentCheckConfigurator__item--title-wrapper">
                        {translate(
                          'weightLossTreatmentCheck.configurator.elevatedLipoprotein',
                        )}
                        <span className="weightLossTreatmentCheckConfigurator__item--explanation">
                          {translate(
                            'weightLossTreatmentCheck.configurator.elevatedLipoproteinExplanation',
                          )}
                        </span>
                      </div>
                      <CustomSelect
                        emptyLabel={translate(
                          'weightLossTreatmentCheck.configurator.nothingSelected',
                        )}
                        options={[
                          {
                            value: 'true',
                            label: translate(
                              'weightLossTreatmentCheck.configurator.detected',
                            ),
                          },
                          {
                            value: 'false',
                            label: translate(
                              'weightLossTreatmentCheck.configurator.notDetected',
                            ),
                          },
                        ]}
                        value={elevatedLipoprotein}
                        onChange={handleElevatedLipoproteinChange}
                        error={errors.elevatedLipoprotein}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="weightLossTreatmentCheckConfigurator__item">
                      <div className="weightLossTreatmentCheckConfigurator__item--title-wrapper">
                        {translate(
                          'weightLossTreatmentCheck.configurator.elevatedBloodSugar',
                        )}
                        <span className="weightLossTreatmentCheckConfigurator__item--explanation">
                          {translate(
                            'weightLossTreatmentCheck.configurator.elevatedBloodSugarExplanation',
                          )}
                        </span>
                      </div>
                      <CustomSelect
                        emptyLabel={translate(
                          'weightLossTreatmentCheck.configurator.nothingSelected',
                        )}
                        options={[
                          {
                            value: 'true',
                            label: translate(
                              'weightLossTreatmentCheck.configurator.detected',
                            ),
                          },
                          {
                            value: 'false',
                            label: translate(
                              'weightLossTreatmentCheck.configurator.notDetected',
                            ),
                          },
                        ]}
                        value={elevatedBloodSugar}
                        onChange={handleElevatedBloodSugarChange}
                        error={errors.elevatedBloodSugar}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="weightLossTreatmentCheckConfigurator__item">
                      <div className="weightLossTreatmentCheckConfigurator__item--title-wrapper">
                        {translate(
                          'weightLossTreatmentCheck.configurator.indicationForFattyLiver',
                        )}
                        <span className="weightLossTreatmentCheckConfigurator__item--explanation">
                          {translate(
                            'weightLossTreatmentCheck.configurator.indicationForFattyLiverExplanation',
                          )}
                        </span>
                      </div>
                      <CustomSelect
                        emptyLabel={translate(
                          'weightLossTreatmentCheck.configurator.nothingSelected',
                        )}
                        options={[
                          {
                            value: 'true',
                            label: translate(
                              'weightLossTreatmentCheck.configurator.detected',
                            ),
                          },
                          {
                            value: 'false',
                            label: translate(
                              'weightLossTreatmentCheck.configurator.notDetected',
                            ),
                          },
                        ]}
                        value={indicationForFattyLiver}
                        onChange={handleIndicationForFattyLiverChange}
                        error={errors.indicationForFattyLiver}
                        disabled={readOnly}
                      />
                    </div>
                  </div>
                  <div className="weightLossTreatmentCheckConfigurator__section">
                    <div className="weightLossTreatmentCheckConfigurator__item--title">
                      {translate(
                        'weightLossTreatmentCheck.configurator.treatmentOptions',
                      )}
                    </div>
                    {/* Zechmann patients get the habit change program, everyone else nutrition advice */}
                    {isZechmannPartner ? (
                      <>
                        <div className="weightLossTreatmentCheckConfigurator__item">
                          <div className="weightLossTreatmentCheckConfigurator__item--title-wrapper">
                            {translate(
                              'weightLossTreatmentCheck.configurator.habitChangeProgram',
                            )}
                            <span className="weightLossTreatmentCheckConfigurator__item--explanation">
                              {translate(
                                'weightLossTreatmentCheck.configurator.habitChangeProgramExplanation',
                              )}
                            </span>
                          </div>
                          <CustomSelect
                            emptyLabel={translate(
                              'weightLossTreatmentCheck.configurator.nothingSelected',
                            )}
                            options={[
                              {
                                value: 'true',
                                label: translate(
                                  'weightLossTreatmentCheck.configurator.eligible',
                                ),
                              },
                              {
                                value: 'false',
                                label: translate(
                                  'weightLossTreatmentCheck.configurator.notEligible',
                                ),
                              },
                            ]}
                            value={habitChangeProgram}
                            onChange={handleHabitChangeProgramChange}
                            error={errors.habitChangeProgram}
                            disabled={readOnly}
                          />
                        </div>
                        {habitChangeProgram === 'false' && (
                          <div className="weightLossTreatmentCheckConfigurator__item">
                            <div>
                              {translate(
                                'weightLossTreatmentCheck.configurator.reasonsForNotEligible',
                              )}
                            </div>
                            <CustomSelect
                              emptyLabel={translate(
                                'weightLossTreatmentCheck.configurator.nothingSelected',
                              )}
                              options={[
                                {
                                  value: 'bmiToLow',
                                  label: translate(
                                    'weightLossTreatmentCheck.configurator.bmiToLow',
                                  ),
                                },
                                {
                                  value: 'pregnantOrBreastfeeding',
                                  label: translate(
                                    'weightLossTreatmentCheck.configurator.pregnantOrBreastfeeding',
                                  ),
                                },
                                {
                                  value: 'prohibitiveHealthConditions',
                                  label: translate(
                                    'weightLossTreatmentCheck.configurator.prohibitiveHealthConditions',
                                  ),
                                },
                                {
                                  value: 'other',
                                  label: translate(
                                    'weightLossTreatmentCheck.configurator.other',
                                  ),
                                },
                              ]}
                              value={reasonsForNotEligibleForHabitChangeProgram}
                              onChange={
                                handleReasonsForNotEligibleForHabitChangeProgramChange
                              }
                              error={
                                errors.reasonsForNotEligibleForHabitChangeProgram
                              }
                              disabled={readOnly}
                            />
                          </div>
                        )}
                        {habitChangeProgram === 'false' &&
                          reasonsForNotEligibleForHabitChangeProgram ===
                            'other' && (
                            <div className="weightLossTreatmentCheckConfigurator__item">
                              <div>
                                {translate(
                                  'weightLossTreatmentCheck.configurator.customReasonForNotEligibleForHabitChangeProgram',
                                )}
                              </div>
                              <TextField
                                value={
                                  customReasonForNotEligibleForHabitChangeProgram
                                }
                                onChange={
                                  handleCustomReasonForNotEligibleForHabitChangeProgramChange
                                }
                                error={
                                  errors.customReasonForNotEligibleForHabitChangeProgram
                                }
                                helperText={
                                  errors.customReasonForNotEligibleForHabitChangeProgram
                                    ? translate('ra.validation.required')
                                    : ''
                                }
                                disabled={readOnly}
                              />
                            </div>
                          )}
                      </>
                    ) : (
                      <>
                        <div className="weightLossTreatmentCheckConfigurator__item">
                          <div className="weightLossTreatmentCheckConfigurator__item--title-wrapper">
                            {translate(
                              'weightLossTreatmentCheck.configurator.nutritionAdviceProgram',
                            )}
                            <span className="weightLossTreatmentCheckConfigurator__item--explanation">
                              {translate(
                                'weightLossTreatmentCheck.configurator.nutritionAdviceProgramExplanation',
                              )}
                            </span>
                          </div>
                          <CustomSelect
                            emptyLabel={translate(
                              'weightLossTreatmentCheck.configurator.nothingSelected',
                            )}
                            options={[
                              {
                                value: 'true',
                                label: translate(
                                  'weightLossTreatmentCheck.configurator.eligible',
                                ),
                              },
                              {
                                value: 'false',
                                label: translate(
                                  'weightLossTreatmentCheck.configurator.notEligible',
                                ),
                              },
                            ]}
                            value={nutritionAdviceProgram}
                            onChange={handleNutritionAdviceProgramChange}
                            error={errors.nutritionAdviceProgram}
                            disabled={readOnly}
                          />
                        </div>
                        {nutritionAdviceProgram === 'false' && (
                          <div className="weightLossTreatmentCheckConfigurator__item">
                            <div>
                              {translate(
                                'weightLossTreatmentCheck.configurator.reasonsForNotEligible',
                              )}
                            </div>
                            <CustomSelect
                              emptyLabel={translate(
                                'weightLossTreatmentCheck.configurator.nothingSelected',
                              )}
                              options={[
                                {
                                  value: 'bmiToLow',
                                  label: translate(
                                    'weightLossTreatmentCheck.configurator.bmiToLow',
                                  ),
                                },
                                {
                                  value: 'pregnantOrBreastfeeding',
                                  label: translate(
                                    'weightLossTreatmentCheck.configurator.pregnantOrBreastfeeding',
                                  ),
                                },
                                {
                                  value: 'prohibitiveHealthConditions',
                                  label: translate(
                                    'weightLossTreatmentCheck.configurator.prohibitiveHealthConditions',
                                  ),
                                },
                                {
                                  value: 'other',
                                  label: translate(
                                    'weightLossTreatmentCheck.configurator.other',
                                  ),
                                },
                              ]}
                              value={
                                reasonsForNotEligibleForNutritionAdviceProgram
                              }
                              onChange={
                                handleReasonsForNotEligibleForNutritionAdviceProgramChange
                              }
                              error={
                                errors.reasonsForNotEligibleForNutritionAdviceProgram
                              }
                              disabled={readOnly}
                            />
                          </div>
                        )}
                        {nutritionAdviceProgram === 'false' &&
                          reasonsForNotEligibleForNutritionAdviceProgram ===
                            'other' && (
                            <div className="weightLossTreatmentCheckConfigurator__item">
                              <div>
                                {translate(
                                  'weightLossTreatmentCheck.configurator.customReasonForNotEligibleForNutritionAdviceProgram',
                                )}
                              </div>
                              <TextField
                                value={
                                  customReasonForNotEligibleForNutritionAdviceProgram
                                }
                                onChange={
                                  handleCustomReasonForNotEligibleForNutritionAdviceProgramChange
                                }
                                error={
                                  errors.customReasonForNotEligibleForNutritionAdviceProgram
                                }
                                helperText={
                                  errors.customReasonForNotEligibleForNutritionAdviceProgram
                                    ? translate('ra.validation.required')
                                    : ''
                                }
                                disabled={readOnly}
                              />
                            </div>
                          )}
                      </>
                    )}
                    <div className="weightLossTreatmentCheckConfigurator__item">
                      <div className="weightLossTreatmentCheckConfigurator__item--title-wrapper">
                        {translate(
                          'weightLossTreatmentCheck.configurator.weightLossInjections',
                        )}
                        <span className="weightLossTreatmentCheckConfigurator__item--explanation">
                          {translate(
                            'weightLossTreatmentCheck.configurator.weightLossInjectionsExplanation',
                          )}
                        </span>
                      </div>
                      <CustomSelect
                        emptyLabel={translate(
                          'weightLossTreatmentCheck.configurator.nothingSelected',
                        )}
                        options={[
                          {
                            value: 'true',
                            label: translate(
                              'weightLossTreatmentCheck.configurator.eligible',
                            ),
                          },
                          {
                            value: 'false',
                            label: translate(
                              'weightLossTreatmentCheck.configurator.notEligible',
                            ),
                          },
                        ]}
                        value={weightLossInjections}
                        onChange={handleWeightLossInjectionsChange}
                        error={errors.weightLossInjections}
                        disabled={readOnly}
                      />
                    </div>
                    {weightLossInjections === 'false' && (
                      <div className="weightLossTreatmentCheckConfigurator__item">
                        <div className="weightLossTreatmentCheckConfigurator__item--title-wrapper">
                          {translate(
                            'weightLossTreatmentCheck.configurator.reasonsForNotEligible',
                          )}
                        </div>
                        <CustomSelect
                          emptyLabel={translate(
                            'weightLossTreatmentCheck.configurator.nothingSelected',
                          )}
                          options={[
                            {
                              value: 'bmiToLow',
                              label: translate(
                                'weightLossTreatmentCheck.configurator.bmiToLow',
                              ),
                            },
                            {
                              value: 'pregnantOrBreastfeeding',
                              label: translate(
                                'weightLossTreatmentCheck.configurator.pregnantOrBreastfeeding',
                              ),
                            },
                            {
                              value: 'prohibitiveHealthConditions',
                              label: translate(
                                'weightLossTreatmentCheck.configurator.prohibitiveHealthConditions',
                              ),
                            },
                            {
                              value: 'other',
                              label: translate(
                                'weightLossTreatmentCheck.configurator.other',
                              ),
                            },
                          ]}
                          value={reasonsForNotEligibleForWeightLossInjections}
                          onChange={
                            handleReasonsForNotEligibleForWeightLossInjectionsChange
                          }
                          error={
                            errors.reasonsForNotEligibleForWeightLossInjections
                          }
                          disabled={readOnly}
                        />
                      </div>
                    )}
                    {weightLossInjections === 'false' &&
                      reasonsForNotEligibleForWeightLossInjections ===
                        'other' && (
                        <div className="weightLossTreatmentCheckConfigurator__item">
                          <div className="weightLossTreatmentCheckConfigurator__item--title-wrapper">
                            {translate(
                              'weightLossTreatmentCheck.configurator.customReasonForNotEligibleForWeightLossInjections',
                            )}
                          </div>
                          <TextField
                            value={
                              customReasonForNotEligibleForWeightLossInjections
                            }
                            onChange={
                              handleCustomReasonForNotEligibleForWeightLossInjectionsChange
                            }
                            error={
                              errors.customReasonForNotEligibleForWeightLossInjections
                            }
                            helperText={
                              errors.customReasonForNotEligibleForWeightLossInjections
                                ? translate('ra.validation.required')
                                : ''
                            }
                            disabled={readOnly}
                          />
                        </div>
                      )}
                    <div className="weightLossTreatmentCheckConfigurator__item--title">
                      {translate(
                        'weightLossTreatmentCheck.configurator.reimbursement',
                      )}
                    </div>
                    <div className="weightLossTreatmentCheckConfigurator__item">
                      <div className="weightLossTreatmentCheckConfigurator__item--title-wrapper">
                        {translate(
                          'weightLossTreatmentCheck.configurator.hasReimbursement',
                        )}
                        <div className="weightLossTreatmentCheckConfigurator__item--explanation">
                          {translate(
                            'weightLossTreatmentCheck.configurator.reimbursementExplanation',
                          )}
                        </div>
                      </div>
                      <CustomSelect
                        emptyLabel={translate(
                          'weightLossTreatmentCheck.configurator.yesOrNo',
                        )}
                        options={[
                          {
                            value: 'true',
                            label: translate(
                              'weightLossTreatmentCheck.configurator.yes',
                            ),
                          },
                          {
                            value: 'false',
                            label: translate(
                              'weightLossTreatmentCheck.configurator.no',
                            ),
                          },
                        ]}
                        value={hasReimbursement}
                        onChange={handleHasReimbursementChange}
                        error={errors.hasReimbursement}
                        disabled={readOnly}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            {isPersonOverweight !== '' && (
              <div className="weightLossTreatmentCheckConfigurator__section">
                <SectionTitle
                  className="weightLossTreatmentCheckConfigurator__title"
                  title={translate('testResults.configureRecommendedActions')}
                />
                <div className="weightLossTreatmentCheckConfigurator__item">
                  <div>
                    {translate(
                      'weightLossTreatmentCheck.configurator.consultDoctor',
                    )}
                  </div>
                  <CustomSelect
                    emptyLabel={translate(
                      'weightLossTreatmentCheck.configurator.yesOrNo',
                    )}
                    options={[
                      {
                        value: 'true',
                        label: translate(
                          'weightLossTreatmentCheck.configurator.yes',
                        ),
                      },
                      {
                        value: 'false',
                        label: translate(
                          'weightLossTreatmentCheck.configurator.no',
                        ),
                      },
                    ]}
                    value={consultDoctor}
                    onChange={handleConsultDoctorChange}
                    error={errors.consultDoctor}
                    disabled={readOnly}
                  />
                </div>
                {isPersonOverweight === 'true' && (
                  <>
                    <div className="weightLossTreatmentCheckConfigurator__item">
                      <div>
                        {translate(
                          'weightLossTreatmentCheck.configurator.bookConsultationForWeightLossMedication',
                        )}
                      </div>
                      <CustomSelect
                        emptyLabel={translate(
                          'weightLossTreatmentCheck.configurator.yesOrNo',
                        )}
                        options={[
                          {
                            value: 'true',
                            label: translate(
                              'weightLossTreatmentCheck.configurator.yes',
                            ),
                          },
                          {
                            value: 'false',
                            label: translate(
                              'weightLossTreatmentCheck.configurator.no',
                            ),
                          },
                        ]}
                        value={bookConsultationForWeightLossMedication}
                        onChange={
                          handleBookConsultationForWeightLossMedicationChange
                        }
                        error={errors.bookConsultationForWeightLossMedication}
                        disabled={readOnly}
                      />
                    </div>
                    {isZechmannPartner ? (
                      <div className="weightLossTreatmentCheckConfigurator__item">
                        <div>
                          {translate(
                            'weightLossTreatmentCheck.configurator.bookConsultationForWeightLossHabitChangeProgram',
                          )}
                        </div>
                        <CustomSelect
                          emptyLabel={translate(
                            'weightLossTreatmentCheck.configurator.yesOrNo',
                          )}
                          options={[
                            {
                              value: 'true',
                              label: translate(
                                'weightLossTreatmentCheck.configurator.yes',
                              ),
                            },
                            {
                              value: 'false',
                              label: translate(
                                'weightLossTreatmentCheck.configurator.no',
                              ),
                            },
                          ]}
                          value={
                            bookConsultationForWeightLossHabitChangeProgram
                          }
                          onChange={
                            handleBookConsultationForWeightLossHabitChangeProgramChange
                          }
                          error={
                            errors.bookConsultationForWeightLossHabitChangeProgram
                          }
                          disabled={readOnly}
                        />
                      </div>
                    ) : (
                      <div className="weightLossTreatmentCheckConfigurator__item">
                        <div>
                          {translate(
                            'weightLossTreatmentCheck.configurator.bookConsultationForNutritionAdvice',
                          )}
                        </div>
                        <CustomSelect
                          emptyLabel={translate(
                            'weightLossTreatmentCheck.configurator.yesOrNo',
                          )}
                          options={[
                            {
                              value: 'true',
                              label: translate(
                                'weightLossTreatmentCheck.configurator.yes',
                              ),
                            },
                            {
                              value: 'false',
                              label: translate(
                                'weightLossTreatmentCheck.configurator.no',
                              ),
                            },
                          ]}
                          value={bookConsultationForNutritionAdvice}
                          onChange={
                            handleBookConsultationForNutritionAdviceChange
                          }
                          error={errors.bookConsultationForNutritionAdvice}
                          disabled={readOnly}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            <div className="weightLossTreatmentCheckConfigurator__section">
              {/* <div className="weightLossTreatmentCheckConfigurator__item--rowDirection">
              <div>
                {translate(
                  'weightLossTreatmentCheck.configurator.confirmCompletedConsultation',
                )}
              </div>
              <CustomCheckbox
                label={translate(
                  'weightLossTreatmentCheck.configurator.consultationCompleted',
                )}
                disabled={readOnly}
                // We are not saving this info in the DB but form can't be finished without checking this box
                // So we are checking it by default if readOnly is true
                checked={consultationCompleted || readOnly}
                onChange={(event) =>
                  handleConsultationCompletedChange(event.target.checked)
                }
                error={errors.consultationCompleted}
              />
            </div> */}

              <div className="form">
                <SectionTitle
                  className="weightLossTreatmentCheckConfigurator__title"
                  title={translate('testResults.doctorsReport')}
                />
                <TextField
                  className="input"
                  id="doctor-name"
                  label={translate(messages.inputLabel)}
                  value={doctorName}
                  variant="outlined"
                  onChange={handleDoctorNameInputChange}
                  error={errors.doctorName}
                  helperText={
                    errors.doctorName ? translate('ra.validation.required') : ''
                  }
                  disabled={readOnly}
                />
                <TextField
                  className="textarea"
                  id="report"
                  label={translate(messages.textareaLabel)}
                  multiline
                  value={report}
                  rows={8}
                  variant="outlined"
                  onChange={handleReportTextareaChange}
                  error={errors.report}
                  helperText={
                    errors.report ? translate('ra.validation.required') : ''
                  }
                  disabled={readOnly}
                />
                {!readOnly && (
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    className="button"
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress size="24.5px" />
                    ) : (
                      translate(messages.buttonText)
                    )}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </>
      </SimpleShowLayout>
    );
  };

export default WeightLossTreatmentCheckConfigurator;
